import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Icon } from "./Icon";
import { useLanguage } from "../context/LanguageContext";
import { track } from "../utils/analytics";

/* Speak instead of type (24 Oct 2026, mock Four_1 change 9): the search boxes, the pickup
   and drop boxes and the feedback boxes. The phone's own speech-to-text, in the reader's
   language - Chrome on Android sends the sound to Google to turn into words, as its own
   keyboard mic does; nothing is recorded or kept by Sramika, only the words come back.
   A phone or browser without it shows no mic at all. */

const SR = typeof window !== "undefined" ? (window.SpeechRecognition || window.webkitSpeechRecognition) : null;
export const voiceSupported = !!SR;
const LOCALE = { te: "te-IN", hi: "hi-IN", ta: "ta-IN", kn: "kn-IN", en: "en-IN" };

function ListeningSheet({ heard, error, onStop, t }) {
  return createPortal(
    <div className="fixed inset-0 z-[70] flex items-end justify-center" role="dialog" aria-modal="true" aria-label={t("vc_listening")}>
      <button type="button" aria-label={t("vc_stop")} onClick={onStop} className="absolute inset-0 bg-[#0F172A]/40" />
      <div className="relative w-full max-w-lg bg-white rounded-t-[22px] px-5 pt-5 pb-[calc(22px+env(safe-area-inset-bottom,0px))] text-center">
        <p className="text-[15px] font-extrabold text-[#0F172A]">{error ? t(error) : (heard || t("vc_listening"))}</p>
        {!error && (
          <div className="flex justify-center items-center gap-1 h-9 my-2" aria-hidden="true">
            {[10, 22, 32, 18, 28, 12, 24].map((h, i) => (
              <span key={i} className="w-[5px] rounded-full bg-[#7BAE00] animate-pulse" style={{ height: h, animationDelay: `${i * 0.12}s` }} />
            ))}
          </div>
        )}
        <p className="text-[11px] text-[#64748B]">{t("vc_langs")}</p>
        <button type="button" onClick={onStop} aria-label={t("vc_stop")}
          className={`mt-3 mx-auto w-[60px] h-[60px] rounded-full flex items-center justify-center text-white ${error ? "bg-[#64748B]" : "bg-[#E11D48] shadow-[0_0_0_8px_rgba(225,29,72,0.15)]"}`}>
          <Icon name={error ? "x" : "mic"} size={24} stroke="#FFFFFF" strokeWidth={2.4} />
        </button>
      </div>
    </div>,
    document.body,
  );
}

/** A mic button: the words heard go to onText. `where` names the box, for the counts. */
export function VoiceButton({ onText, where, className = "", size = 16 }) {
  const { t, lang } = useLanguage();
  const [open, setOpen] = useState(false);
  const [heard, setHeard] = useState("");
  const [error, setError] = useState("");
  const recRef = useRef(null);

  useEffect(() => () => { try { recRef.current?.abort(); } catch { /* already stopped */ } }, []);
  if (!SR) return null;

  const stop = () => { try { recRef.current?.abort(); } catch { /* already stopped */ } setOpen(false); };
  const start = () => {
    setHeard(""); setError(""); setOpen(true);
    const rec = new SR();
    recRef.current = rec;
    rec.lang = LOCALE[lang] || "en-IN";
    rec.interimResults = true;
    rec.maxAlternatives = 1;
    let done = false, failed = false;
    rec.onresult = (e) => {
      const text = Array.from(e.results).map((r) => r[0].transcript).join(" ").trim();
      setHeard(text);
      if (e.results[e.results.length - 1].isFinal && text && !done) {
        done = true;
        track("voice_used", { where, lang });
        onText(text);
        setTimeout(() => setOpen(false), 250);
      }
    };
    rec.onerror = (e) => {
      failed = true;
      setError(e.error === "not-allowed" || e.error === "service-not-allowed" ? "vc_errMic" : e.error === "no-speech" ? "vc_errNone" : "vc_errOther");
    };
    // Stopped without words or an error (a pause): close. An error stays until tapped.
    rec.onend = () => { if (!done && !failed) setOpen(false); };
    try { rec.start(); } catch { setError("vc_errOther"); }
  };

  return (
    <>
      <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={start} aria-label={t("vc_speak")} title={t("vc_speak")}
        className={`shrink-0 flex items-center justify-center rounded-full bg-[#F1F7E3] text-[#3F5E00] active:scale-95 ${className}`}>
        <Icon name="mic" size={size} strokeWidth={2.2} />
      </button>
      {open && <ListeningSheet heard={heard} error={error} onStop={stop} t={t} />}
    </>
  );
}
